import { colors } from '@lmc/common/constants'
import { EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { postJSON } from 'common/services/api'
import { getNominative } from 'common/utils'
import { useCanEditServiceUserData } from 'components/AccessControl'
import { HelpLink, Icon, ListItem, Spinner } from 'components/generic'
import type { IconName } from 'components/generic/Icon/Icon'
import { ProTag } from 'components/ProTag'
import dayjs from 'dayjs'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { every, find, some } from 'lodash'
import Operations from 'operations'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { history } from 'store'
import ConfirmationDialog from '../../../../LEGACY/ConfirmationDialog'
import { getResidentInitialAssessments as getResidentInitialAssessmentsSelector } from '../../../../selectors/InitialAssessments'
import { InitialAssessmentPaywall } from './components'
import {
  clearInitialAssessmentFlowStartedAt,
  ensureInitialAssessmentFlowStartedAt,
  getInitialAssessmentFlowDurationMs,
  startInitialAssessmentFlow
} from './initialAssessmentFlowTiming'
import { Components } from './ResidentInitialAssessmentDashboardScreen.styled'

const isDesktop = isBrowser || isTablet

const SUPPORT_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3376538-how-do-i-use-initial-assessments'

const getInfo = (
  carePlanNominative: string,
  preferredPeopleNominative: string
) =>
  `If you start an Initial Assessment for this ${getNominative(
    preferredPeopleNominative,
    'resident'
  )}, you won't be able to update their ${getNominative(
    carePlanNominative,
    'Care Plan'
  )} until the assessment is complete.`

type InitialAssessmentListItem = {
  id?: string
  status?: string
  editedOn?: string
  editedBy?: { name?: { first: string; last: string } }
  carePlanAdl: { id: string; name: string; icon: string; color: string }
}

type Resident = {
  id: string
  status: string
  isActiveInitialAssessment?: boolean
  name: { first: string; last: string }
  preferredName?: string
}

const getResidentInitialAssessments = getResidentInitialAssessmentsSelector as (
  state: unknown,
  props: { router: { params: { id: string } } }
) => InitialAssessmentListItem[] | null | undefined

export const ResidentInitialAssessmentDashboardScreen = () => {
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const sendEvent = useMixpanelEvent()
  const [confirmationOpen, setConfirmationOpen] = useState(false)

  const carePlans = useSelector((state: any) => state.data['care-plans'])
  const features = useSelector((state: any) => state.global.home)

  const canEditServiceUserData = useCanEditServiceUserData()

  const selectResidentInitialAssessments = useMemo(
    () => (state: any) =>
      getResidentInitialAssessments(state, {
        router: { params: { id: id ?? '' } }
      }) ?? [],
    [id]
  )
  const initialAssessments = useSelector(
    selectResidentInitialAssessments
  ) as InitialAssessmentListItem[]
  const resident = useSelector((state: any) =>
    find(state.data.residents, { id })
  ) as Resident | undefined
  const carePlanNominative = useSelector(
    (state: any) => state.global.home.preferredCarePlansNominative
  )
  const preferredPeopleNominative = useSelector(
    (state: any) => state.global.home.preferredPeopleNominative
  )
  const hasCarePlans = useSelector(
    (state: any) => state.global.home.features.carePlans?.enabled
  )

  const residentInPreAssessment = useCallback(() => {
    return (
      !!resident &&
      (resident.status === 'pre-assessment' ||
        !!resident.isActiveInitialAssessment)
    )
  }, [resident])

  useEffect(() => {
    if (features?.carePlans && id) {
      dispatch(Operations.loadList('initial-assessments', { resident: id }))
      dispatch(
        Operations.loadList('care-plans', {
          resident: id,
          isRevision: false
        })
      )
    }
  }, [dispatch, features?.carePlans, id])

  useEffect(() => {
    if (residentInPreAssessment() && id) {
      ensureInitialAssessmentFlowStartedAt(id)
    }
  }, [id, residentInPreAssessment])

  const redirectToItem = useCallback(
    (item: { id: string }) => {
      if (!id) return
      history.push(`/residents/${id}/initial-assessments/${item.id}/edit`)
    },
    [id]
  )

  const createInitialAssessment = useCallback(
    ({
      carePlanAdl,
      resident: residentId
    }: {
      carePlanAdl: string
      resident: string
    }) => {
      postJSON({
        url: 'list/initial-assessments',
        method: 'POST',
        json: { carePlanAdl, resident: residentId }
      })
        .then((res) => redirectToItem(res.result))
        .catch((e) => {
          console.log(e)
        })
    },
    [redirectToItem]
  )

  const startInitialAssessment = useCallback(() => {
    if (!id) return
    postJSON({
      url: `list/residents/${id}`,
      method: 'PUT',
      json: { isActiveInitialAssessment: true }
    })
      .then(() => {
        startInitialAssessmentFlow(id)
        dispatch(Operations.loadListItem('residents', id))
      })
      .catch((e) => console.log(e))
  }, [dispatch, id])

  const completeInitialAssessment = useCallback(() => {
    if (!id) return
    const residentId = resident?.id ?? id
    sendEvent(MixpanelEvents.INITIAL_ASSESSMENT_COMPLETED, {
      durationMs: getInitialAssessmentFlowDurationMs(residentId),
      residentId
    })
    clearInitialAssessmentFlowStartedAt(residentId)
    dispatch(Operations.loadListItem('residents', id))
    history.push(`/residents/${id}/care-plans`)
  }, [dispatch, id, resident?.id, sendEvent])

  const createCarePlan = useCallback(() => {
    if (!id) return
    postJSON({
      url: `list/residents/${id}/createCarePlan`,
      method: 'POST'
    })
      .then(() => completeInitialAssessment())
      .catch((e) => console.log(e))
  }, [completeInitialAssessment, id])

  const isNextStepDisabled = useCallback(() => {
    return (
      !initialAssessments?.length ||
      some(initialAssessments, ({ status }) => !status || status !== 'done')
    )
  }, [initialAssessments])

  const goToNextStep = useCallback(() => {
    if (!id) return
    history.push(`/residents/${id}/initial-assessments/complete`)
  }, [id])

  const handleSelect = useCallback(
    (item: InitialAssessmentListItem) => {
      if (!item.id && id) {
        createInitialAssessment({
          carePlanAdl: item.carePlanAdl.id,
          resident: id
        })
      } else if (item.id) {
        redirectToItem(item as { id: string })
      }
    },
    [createInitialAssessment, id, redirectToItem]
  )

  const isStartButtonEnabled = useCallback(() => {
    return (
      !carePlans?.length ||
      every(
        carePlans,
        (c: { nextReviewDate?: unknown; revisions: number }) =>
          !c.nextReviewDate && c.revisions === 1
      )
    )
  }, [carePlans])

  const showStartInitialAssessment = useCallback(() => {
    return (
      !residentInPreAssessment() &&
      every(initialAssessments, (assessment) => !assessment.editedBy)
    )
  }, [initialAssessments, residentInPreAssessment])

  const getSubLabel = (item: InitialAssessmentListItem) => {
    if (!item?.editedOn) return 'No updates yet'
    const carer = item.editedBy
    const date = dayjs(item.editedOn).format('DD-MM-YYYY • HH:mm')
    if (!carer?.name) return <span>Updated {date}</span>
    return (
      <span>
        Updated {date} • {`${carer.name.first} ${carer.name.last}`}
      </span>
    )
  }

  const renderRightIcon = (item: InitialAssessmentListItem) => {
    let opts: { color: string; label: string; icon: string } | undefined
    switch (item.status) {
      case 'done':
        opts = {
          color: colors.green,
          label: 'Done',
          icon: 'tick-in-circle'
        }
        break
      case 'draft':
        opts = {
          color: colors.orange,
          label: 'Draft',
          icon: 'pencil'
        }
        break
      default:
        return null
    }

    return (
      <Components.StatusIconWrapper>
        <Components.StatusLabel>{opts.label}</Components.StatusLabel>
        <Icon name={opts.icon as IconName} fill={opts.color} size={24} />
      </Components.StatusIconWrapper>
    )
  }

  if (!hasCarePlans) {
    return <InitialAssessmentPaywall />
  }

  if (
    !carePlans ||
    !features?.carePlans ||
    !resident ||
    !initialAssessments?.length
  ) {
    return <Spinner />
  }

  const fullName = `${resident.name.first} ${resident.name.last}`
  const inPreAssessment = residentInPreAssessment()
  const startEnabled = isStartButtonEnabled()
  const showStart = showStartInitialAssessment()

  return (
    <div data-testid="initial-assessment-dashboard-screen">
      <Components.Content data-testid="initial-assessment-content">
        <PageHeader
          title={`${fullName}'s Initial Assessment`}
          subtitle={
            inPreAssessment
              ? 'Complete all sections to finalise the Initial Assessment'
              : undefined
          }
          onBack={
            !isDesktop && id
              ? () => history.replace?.(`/residents/${id}/profile`)
              : undefined
          }
          sticky
          data-testid="initial-assessment-header"
        >
          <ProTag data-testid="initial-assessment-pro-tag" />
          {showStart && (
            <>
              <Components.StartAssessmentButton
                id="start-initial-assessment"
                disabled={!startEnabled}
                disabledByPermission={!canEditServiceUserData}
                onClick={() => setConfirmationOpen((open) => !open)}
                variant="primary"
                data-testid="start-initial-assessment-button"
              >
                Start Initial Assessment
              </Components.StartAssessmentButton>
              <ConfirmationDialog
                buttonText="Start"
                buttonType="confirm"
                confirmationType="warning"
                isOpen={confirmationOpen}
                onCancel={() => setConfirmationOpen((open) => !open)}
                onConfirmation={startInitialAssessment}
                data-testid="initial-assessment-confirmation-dialog"
              >
                <div data-testid="initial-assessment-confirmation-content">
                  <p>
                    {getInfo(carePlanNominative, preferredPeopleNominative)}
                  </p>
                </div>
              </ConfirmationDialog>
            </>
          )}
        </PageHeader>
        <Components.Dashboard data-testid="initial-assessment-dashboard">
          {showStart ? (
            <div data-testid="initial-assessment-no-data">
              <EmptyState
                title={
                  startEnabled
                    ? `${
                        resident.preferredName || resident.name.first
                      } currently doesn't have an Initial Assessment.`
                    : `${
                        resident.preferredName || resident.name.first
                      }'s Initial Assessment can no longer be added here, because changes have been made to their ${carePlanNominative}.`
                }
                icon={<NoDataIcon />}
                description={
                  startEnabled
                    ? `You can add one here as long as no changes have been made to their ${carePlanNominative}.`
                    : `If you'd like to upload an Initial Assessment, you can do this in Documents.`
                }
                data-testid="initial-assessment-empty-state"
              />
            </div>
          ) : (
            <Components.ListContainer data-testid="initial-assessment-list">
              {initialAssessments.map((item, i) => {
                const adl = item.carePlanAdl
                return (
                  <ListItem
                    icon={adl.icon}
                    iconColor={adl.color}
                    key={i}
                    label={adl.name}
                    onSelect={() => handleSelect(item)}
                    subLabel={getSubLabel(item)}
                    data-testid={`initial-assessment-item-${i}`}
                  >
                    {renderRightIcon(item)}
                  </ListItem>
                )
              })}
            </Components.ListContainer>
          )}
        </Components.Dashboard>
        <HelpLink
          supportLink={SUPPORT_ARTICLE}
          data-testid="initial-assessment-help-link"
        />
      </Components.Content>
      {(resident.status === 'pre-assessment' ||
        resident.isActiveInitialAssessment) && (
        <Components.Footer className="lmc-fixed-footer">
          <Components.ActionButton
            id="initial-assessment-next-step"
            disabled={isNextStepDisabled()}
            onClick={
              resident.isActiveInitialAssessment ? createCarePlan : goToNextStep
            }
            variant="primary"
            data-testid="finalise-initial-assessment-button"
          >
            Finalise Initial Assessment
          </Components.ActionButton>
        </Components.Footer>
      )}
    </div>
  )
}

export default ResidentInitialAssessmentDashboardScreen
